import type { InjectFunction, InjectMessage, Message } from "preview-protocol";
import { useMemo, useRef, useState } from "react";

export interface ICommonPreviewProtocol<
  MessageFromDevToolToPreview extends Message,
  MessageFromPreviewToDevtool extends Message,
> {
  sendMessage: (message: MessageFromDevToolToPreview) => void;
  injectAndInvoke: <Scope extends Record<string, unknown>>(
    func: InjectFunction<
      MessageFromDevToolToPreview,
      MessageFromPreviewToDevtool,
      Scope
    >,
    scope: Scope,
  ) => void;
  onMessage: (
    subscriber: (message: MessageFromPreviewToDevtool) => void,
  ) => () => void;
}

export type PreviewRefCallback = ((
  previewEl: HTMLIFrameElement | null,
) => void) & {
  current: HTMLIFrameElement | null;
};

type PreviewProtocol =
  | {
      status: "CONNECTED";
      previewEl: HTMLIFrameElement;
    }
  | {
      status: "DISCONNECTED";
    };

export const usePreviewProtocol = <
  MessageFromDevToolToPreview extends Message,
  MessageFromPreviewToDevtool extends Message,
>(
  src: string,
): ICommonPreviewProtocol<
  MessageFromDevToolToPreview,
  MessageFromPreviewToDevtool
> & {
  previewRef: PreviewRefCallback;
  refresh: () => void;
  setIframeUrl: (url: string) => void;
  status: PreviewProtocol["status"];
  iframeRef: HTMLIFrameElement | undefined;
} => {
  const subscribers = useRef<
    Set<(message: MessageFromPreviewToDevtool) => void>
  >(new Set());
  const [state, setState] = useState<PreviewProtocol>({
    status: "DISCONNECTED",
  });

  const injectAndInvoke: ICommonPreviewProtocol<
    MessageFromDevToolToPreview,
    MessageFromPreviewToDevtool
  >["injectAndInvoke"] = (func, scope) => {
    if (state.status === "CONNECTED") {
      const injectMessage: InjectMessage = {
        code: `exports.activate = ${func.toString()}`,
        type: "INJECT_AND_INVOKE",
        uid: "TODO", // TODO: supply a unique id from the workspace feature.
        scope,
      };
      state.previewEl.contentWindow?.postMessage(injectMessage, "*");
    }
  };

  const onMessage = (
    subscriber: (message: MessageFromPreviewToDevtool) => void,
  ) => {
    subscribers.current.add(subscriber);

    return () => {
      subscribers.current.delete(subscriber);
    };
  };

  const sendMessage = (message: MessageFromDevToolToPreview) => {
    if (state.status === "CONNECTED") {
      state.previewEl.contentWindow?.postMessage(message, "*");
    }
  };

  const previewElRef = useRef<HTMLIFrameElement>();
  const previewRef = useMemo<PreviewRefCallback>(() => {
    const previewRefFunc: PreviewRefCallback = (previewEl) => {
      if (!previewEl || !src) return;

      previewRefFunc.current = previewEl;
      previewElRef.current = previewEl;

      const origin = new URL(src).origin;

      const windowListener = (event: MessageEvent) => {
        if (!previewEl.parentNode) {
          return unsubscribe();
        }

        /*
        Only handle messages from preview window
        Ref: https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage#security_concerns
        */
        if (event.origin !== origin) return;

        /*
        Only handle messages from the `previewEl` iFrame
        */
        if (event.source !== previewEl.contentWindow) return;

        if (event.data.type === "PREVIEW_UNLOADING") {
          setState({ status: "DISCONNECTED" });
        }

        subscribers.current.forEach((onMessage) => {
          onMessage(event.data);
        });
      };

      const unsubscribe = () => {
        window.removeEventListener("message", windowListener);
        previewEl.removeEventListener("load", onPreviewLoad);
      };

      const onPreviewLoad = () => {
        if (!previewEl.parentNode) {
          return unsubscribe();
        }

        setState({
          status: "CONNECTED",
          previewEl,
        });
      };

      window.addEventListener("message", windowListener);
      previewEl.addEventListener("load", onPreviewLoad);
    };

    previewRefFunc.current = null;

    return previewRefFunc;
  }, [src]);

  const refresh = () => {
    setState({ status: "DISCONNECTED" });
    /**
     * Force the iFrame to reload src
     * @link https://css-tricks.com/snippets/jquery/force-iframe-to-reload/
     */
    if (previewElRef.current) {
      // eslint-disable-next-line no-self-assign
      previewElRef.current.src = src;
    }
  };

  const setIframeUrl = (url: string) => {
    setState({ status: "DISCONNECTED" });

    if (previewElRef.current) {
      // eslint-disable-next-line no-self-assign
      previewElRef.current.src = url;
    }
  };

  return {
    previewRef,
    iframeRef: previewElRef.current,
    status: state.status,
    injectAndInvoke,
    sendMessage,
    refresh,
    onMessage,
    setIframeUrl,
  };
};
